import * as React from 'react';
import * as i18next from 'i18next';
import { t } from 'i18next';

import { FileFormat, FileFormats } from '../constants/FileFormat';
import { ViewSettingsActions } from '../actions/ViewSettingsActions';
import { DialogActions, UnsupportedFileError } from '../actions/DialogActions';
import { SceneActions } from '../actions/SceneActions';
import { AppDocumentContainer } from './AppDocumentContainer';
import { GlobalHelper } from './GlobalHelper';
import { Stats } from './Stats';
import { PersistentStore } from '../stores/PersistentStore';
import { ScreenshotHelper } from './ScreenshotHelper';
import { ShareImageSize } from '../constants/LayoutConstants';
import { ProjectManagerStore } from '../stores/ProjectManagerStore';
import { ProjectSettingsStore } from '../stores/ProjectSettingsStore';
import { AppConfigurationStore } from '../stores/AppConfigurationStore';
import { ArticleActions } from '../actions/ArticleActions';
import { ArticleStore } from '../stores/ArticleStore';
import { UIHelper, PromiseCallbacks } from './UIHelper';
import { PconLoginStore } from '../stores/PconLoginStore';
import { DownloadHelper } from './DownloadHelper';
import { InteractionQueue } from './InteractionQueue';
import { XLSXDocument } from './XLSXDocument';
import { PDFMaker } from './PDFMaker';
import { LoadingCancelable, LoadingCancelableProps } from '../components/LoadingCancelable';
import { PromiseQueue } from './PromiseQueue';

// Submodules
import * as AppFileApiWrapper from '../submodules/app-api/Filesystem/Wrapper';
import { Dict } from '../submodules/utils/Types';

import utils = egr.wcf.utils;
import { downloadUrl } from '../submodules/utils/Download';
import { sendMessage } from '../submodules/app-api/Communication';
import { ArStore } from '../stores/ArStore';
import { requestArCameraFrame } from '../submodules/app-api/AppAction';
import { ArScreenshot, ArCore } from '../submodules/ar-manager/ArCore';
import { awaitOnce, EventData } from '../submodules/utils/Promise';
import { shareContent, shareUrl } from '../submodules/app-api/Share';
import { readBinaryFile } from '../submodules/app-api/Filesystem/Helper';

interface AppRequest {
    type: string;
}

interface WebAction extends AppRequest {
    action: string;
    arguments?: any;
}

interface RequestProgress {
    time: number;
    request_id: number;
}

interface DownloadProgress {
    size: number;
    fetched: number;
}

interface DownloadRequestProgress extends RequestProgress {
    downloads: Array<DownloadProgress>;
}

interface LanguageSetting {
    lng: string;
    res?: any;
}

const MessageTimeout: number = 10000;
const DownloadCancelMessage: string = '17bfc793-87d1-482f-9da8-6906a66a078c: Download canceled';

interface ShareContent {
    format: FileFormat;
    content: string;
    filename?: string;
    save?: boolean;
    pickPath?: boolean;
}

export abstract class AppHelper {
    public static msgHook: Array<(msg: {[id: string]: any}) => void> = [];

    public static setLanguage(language: string): Promise<any> {
        const fallbackTranslation: {lng: string, res?: any} = {lng: 'en'};
        const allowedLangs: Array<string> = [
            'da', 'de', 'en', 'es', 'fr', 'it', 'nl', 'pl', 'pt', 'ro', 'tr'
        ];
        return new Promise<void>((resolve: () => void, reject: () => void) => {
            if (allowedLangs.indexOf(language) !== -1) {
                egr.wcf.utils.async.ajax<Dict<string>>('GET', 'i18n/' + language + '/translation.json', null, {
                    dataType: 'json'
                }).then(
                    (data: Dict<string>): LanguageSetting => {return {lng: language, res: data}; },
                    (): LanguageSetting => { return fallbackTranslation; }
                ).then(
                    (settings: LanguageSetting): void => {
                        const mapping: any = {};
                        if (settings.res != null) {
                            mapping[settings.lng] = {
                                translation: settings.res
                            };
                        }
                        i18next.init(
                            {
                                lng: settings.lng,
                                resources: mapping,
                                returnEmptyString: false,
                                nsSeparator: '',
                                keySeparator: ''
                            },
                            resolve
                        );
                    },
                    reject
                );
            } else {
                reject();
            }
        });
    }

    public static importFromFilesystem(): void {
        sendMessage(
            {
                type: 'appaction',
                action: 'import',
                arguments: {
                    file_types: ['com.easterngraphics.pbox', 'com.easterngraphics.pec']
                }
            },
            true
        );
    }

    public static shareLinkContent(
        format: ExportFormat,
        pickPath: boolean = false,
        onProgress?: (data: DownloadRequestProgress) => void
    ): Promise<void> {
        Stats.sendEvent({
            category: 'Send',
            action: 'app',
            label: format
        });

        let transactionId: number | null = null;
        const sharingSteps: PromiseQueue = new PromiseQueue();
        const listenableEvent: utils.ListenableEvent<number, unknown> = new utils.ListenableEvent<number, unknown>(null);
        const loadingCancelableProps: LoadingCancelableProps = {
            label: t('Download'),
            cancelCallback: (): void => {
                if (transactionId != null) {
                    AppHelper.cancelDownloadRequest(transactionId);
                }

                sharingSteps.cancle((): never => {
                    throw new Error(DownloadCancelMessage);
                });
            },
            loadingAnimationProps: {
                counterProps: {
                    listenableEvent: listenableEvent
                }
            }
        };

        return ViewSettingsActions.withLock<void>(
            async (): Promise<void> => {
                try {
                    let cancled: boolean = false;
                    const url: string | null = await sharingSteps.add<string | null>((): Promise<string | null> => {
                        return DownloadHelper.getExportUrl(format).then((result: string | null) => {
                            return result;
                        }).catch((reason: any) => {
                            if (reason == null) {
                                cancled = true;
                            }
                            return null;
                        });
                    });
                    if (utils.string.isNullOrEmpty(url)) {
                        if (!cancled) {
                            UIHelper.showMessage(t('The CAD file could not be generated.'));
                        }
                        throw new Error(DownloadCancelMessage);
                    }
                    await sharingSteps.add((): Promise<void> => {
                        return sendMessage(
                            {
                                type: 'share',
                                action: 'fetch',
                                arguments: {
                                    content: url,
                                    mimetype: FileFormats[format].mimetype, // FixMe
                                    filename: ProjectManagerStore.getProjectName(true) + '.' + FileFormats[format].extension,
                                    save: pickPath,
                                    pickPath: pickPath
                                }
                            },
                            false,
                            MessageTimeout,
                            {
                                transactionId: (value: number): void => {
                                    transactionId = value;
                                },
                                progress: (data: DownloadRequestProgress): void => {
                                    listenableEvent.trigger(
                                        Math.round(
                                            data.downloads.reduce(
                                                (acc: number, download: DownloadProgress): number => {
                                                    // Note: -1 is send if the size is unknown
                                                    if (download.size < 1 || isNaN(acc)) {
                                                        return NaN;
                                                    }

                                                    const percentage: number = download.fetched / download.size;

                                                    if (acc == null) {
                                                        return percentage;
                                                    }

                                                    return (acc + percentage) / 2;
                                                },
                                                0
                                            ) * 100
                                        )
                                    );
                                }
                            }
                        );
                    });
                } catch (error) {
                    if (error.msg !== DownloadCancelMessage) {
                        throw error;
                    }
                }
            },
            React.createElement(LoadingCancelable, loadingCancelableProps)
        );
    }

    public static shareContent(content: ShareContent): Promise<void> {
        return ViewSettingsActions.withLock<void>((): Promise<void> => {
            return sendMessage(
                {
                    type: 'share',
                    action: 'decode',
                    arguments: {
                        content: content.content,
                        mimetype: FileFormats[content.format].mimetype,
                        filename: ProjectManagerStore.getProjectName(true, content.filename) + '.' + FileFormats[content.format].extension,
                        save: content.save === true,
                        pickPath: content.pickPath === true
                    }
                }
            );
        });
    }

    public static sharePboxFile(pickPath: boolean = false): Promise<void> {
        Stats.sendEvent({
            category: 'Send',
            action: 'app',
            label: 'PBOX'
        });

        return ViewSettingsActions.withLock<void>((): Promise<void> => {
            return UIHelper.waitForLockVisibility().then((): Promise<void> => {
                return ArticleActions.updateArticleList(); // FixMe: update before lock!?
            }).then(() => {
                return GlobalHelper.coreApp.document.saveToZip({
                    // Note: the `compressionLevel` is necessary because
                    // of the ugly hack in the `ProjectSettingsModule.saveDocumentData` function
                    compressionLevel: 4,
                    embedGeometries: true,
                    embedTextures: true
                }).then((data: ArrayBuffer) => {
                    AppHelper.shareContent({
                        format: 'PBOX',
                        content: egr.wcf.utils.arraybuffer.toBase64(data),
                        filename: ProjectManagerStore.getProjectName(true),
                        save: pickPath,
                        pickPath: pickPath
                    });
                });
            });
        });
    }

    public static shareArticleListPDF(pickPath: boolean = false): Promise<void> {
        Stats.sendEvent({
            category: 'Send',
            action: 'app',
            label: 'PDF'
        });

        return ViewSettingsActions.withLock<void>((): Promise<void> => {
            return UIHelper.waitForLockVisibility().then((): Promise<void> => {
                return ArticleActions.updateArticleList(); // FixMe: update before lock!?
            }).then(() => {
                return PDFMaker.generatePDFAsBase64().then((data: string) => {
                    AppHelper.shareContent({
                        format: 'PDF',
                        content: data,
                        filename: t('Article List'),
                        save: pickPath,
                        pickPath: pickPath
                    });
                });
            });
        });
    }

    public static shareArticleListExcel(pickPath: boolean = false): Promise<void> {
        Stats.sendEvent({
            category: 'Send',
            action: 'app',
            label: 'EXCEL'
        });

        return ViewSettingsActions.withLock<void>((): Promise<void> => {
            return UIHelper.waitForLockVisibility().then((): Promise<void> => {
                return ArticleActions.updateArticleList(); // FixMe: update before lock!?
            }).then(() => {
                const xlsx: XLSXDocument = new XLSXDocument(ArticleStore.getState().articleList);
                xlsx.getDocument(UIHelper.showPrices()).then((workbook: any) => {
                    workbook.outputAsync('base64').then((base64: string) => {
                        AppHelper.shareContent({
                            format: 'EXCEL',
                            content: base64,
                            filename: t('Article List'),
                            save: pickPath,
                            pickPath: pickPath
                        });
                    });
                });
            });
        });
    }

    public static shareArticleListHTML(pickPath: boolean = false): Promise<void> {
        return ViewSettingsActions.withLock<void>((): Promise<void> => {
            return UIHelper.waitForLockVisibility().then((): Promise<void> => {
                return ArticleActions.updateArticleList(); // FixMe: update before lock!?
            }).then(() => {
                const htmlContent: string = ArticleStore.getStyledHTML(
                    (ProjectSettingsStore.getState().articleListPrices === 'show' && PconLoginStore.pricesEnabled()),
                    AppConfigurationStore.getState().showArticleNumbers
                );

                if (false) {
                    AppHelper.shareContent({
                        format: 'HTML',
                        content: window.btoa(htmlContent),
                        filename: t('Article List'),
                        save: pickPath,
                        pickPath: pickPath
                    });
                } else {
                    downloadUrl(htmlContent, ProjectManagerStore.getProjectName(true, t('Article List')) + '.html');
                }
            });
        });
    }

    public static shareScreenshot(silent: boolean = false, pickPath: boolean = false): Promise<void> {
        if (silent) {
            Stats.sendEvent({
                category: 'Download',
                action: 'download',
                label: PersistentStore.getState().imageFormat
            });
        } else {
            Stats.sendEvent({
                category: 'Send',
                action: 'app',
                label: PersistentStore.getState().imageFormat
            });
        }

        // the timeout should resolve the promise because the animation
        // is a nice to have feature but not important enough to break the
        // action

        return ViewSettingsActions.withLock<void>(
            (): Promise<void> => {
                return Promise.all<void>([
                    AppHelper.triggerScreenshot(silent, pickPath)
                ]).then((result: Array<void>): void => {
                    // Note: convert from Promise<Array<void>> to Promise<void>
                });
            }
        );
    }

    // debug code: should be removed later on
    public static sendReply(transactionId: number, error: boolean, data?: any): void {
        window.postMessage(
            {
                transaction_id: transactionId,
                type: 'reply',
                error: error,
                data: data
            },
            '*'
        );
    }

    public static async openFile(event: MessageEvent): Promise<void> {
        try {
            await InteractionQueue.add((): Promise<void> => {
                return AppHelper.handleOpenFileAction(event.data);
            });
        } finally {
            await UIHelper.ignoreReject(AppFileApiWrapper.remove(event.data.arguments.path));
        }
    }

    private static cancelDownloadRequest(transactionId: number): void {
        sendMessage(
            {
                type: 'share',
                action: 'cancel',
                arguments: {
                    request_id: transactionId,
                    error_message: DownloadCancelMessage
                }
            },
            true
        );
    }

    private static async triggerScreenshot(silent: boolean = false, pickPath: boolean = false): Promise<void> {
        let content: string | undefined;

        let format: FileFormat = 'PNG';

        if (ArStore.getState().started) {
            ArStore.core.screenshotType = 'blob';
            const promise: Promise<EventData<ArScreenshot, ArCore>> = awaitOnce<ArScreenshot, ArCore>(ArStore.core.eventArScreenshot);
            await requestArCameraFrame();
            const data: EventData<ArScreenshot, ArCore> = await promise;
            if (data.parameter != null && data.parameter.output === 'blob') {
                return shareUrl(
                    URL.createObjectURL(data.parameter.data),
                    'image/png',
                    `screenshot.${format === 'PNG' ? 'png' : 'jpg'}`,
                    silent || pickPath,
                    pickPath
                );
            }
        } else {
            if (PersistentStore.getState().imageFormat === 'JPG') {
                format = 'JPG';
            }
            content = await ScreenshotHelper.getScreenshot({
                width: ShareImageSize.width,
                height: ShareImageSize.height,
                stripUrlScheme: true,
                renderAsync: true,
                addWatermark: true,
                customMimetype: true
            });
            if (utils.string.isNotNullOrEmpty(content)) {
                return shareContent(
                    content,
                    PersistentStore.getState().imageFormat === 'JPG' ? 'image/png' : 'image/jpg',
                    PersistentStore.getState().imageFormat === 'JPG' ? 'screenshot.png' : 'screenshot.jpg',
                    silent || pickPath,
                    pickPath
                );
            }
        }
    }

    private static async handleOpenFileAction(data: WebAction): Promise<void> {
        let fileType: string = data.arguments.type;
        if (utils.string.isNullOrEmpty(fileType) && !utils.string.isNullOrEmpty(data.arguments.filename)) {
            fileType = data.arguments.filename.split('.').pop();
        }

        if (!utils.string.isNullOrEmpty(fileType)) {
            switch (fileType.toLocaleLowerCase()) {
                case 'obk':
                    const obkContent: ArrayBuffer = await readBinaryFile(data.arguments.path);
                    return DialogActions.openProjectFromFile({
                        type: 'OBK',
                        content: new Blob([obkContent])
                    });
                case 'pbox':
                    return DialogActions.openProjectFromFile({
                        type: 'PBOX',
                        content: new AppDocumentContainer(data.arguments.path)
                    });
                case 'pec':
                    const content: ArrayBuffer = await ViewSettingsActions.withLock<ArrayBuffer>(() => {
                        return readBinaryFile(data.arguments.path);
                    });

                    type ActionType = 'import' | 'open' | 'cancel';
                    let action: ActionType = 'open';
                    if (ProjectManagerStore.getCurrentProject() != null) {
                        const desicionPromise: PromiseCallbacks<ActionType> = UIHelper.getPromiseCallbacks<ActionType>();
                        DialogActions.showImportDialog({
                            importCallback: (): void => {
                                desicionPromise.resolve('import');
                            },
                            newSceneCallback: (): void => {
                                desicionPromise.resolve('open');
                            },
                            cancelCallback: (): void => {
                                desicionPromise.resolve('cancel');
                            }
                        });
                        action = await desicionPromise.promise;
                    }

                    switch (action) {
                        case 'open':
                            await DialogActions.openProjectFromFile({
                                type: 'PEC',
                                content: content
                            });
                            break;
                        case 'import':
                            await SceneActions.importPecFile(content);
                            break;
                        default:
                            return;
                    }
                    break;
                default:
                    throw new UnsupportedFileError('Unsupported file type.');
            }
        }
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/AppHelper.ts